/**
 * CodeMirror 6 Editor Component.
 * Manual wrapper with cyberpunk theme and full feature set.
 */

import { useRef, useEffect, useCallback } from "react";
import { EditorState, Compartment } from "@codemirror/state";
import {
  EditorView,
  keymap,
  highlightActiveLine,
  drawSelection,
  dropCursor,
  rectangularSelection,
  crosshairCursor,
  lineNumbers,
  highlightActiveLineGutter,
} from "@codemirror/view";
import { defaultKeymap, history, historyKeymap, indentWithTab } from "@codemirror/commands";
import {
  syntaxHighlighting,
  defaultHighlightStyle,
  indentOnInput,
  bracketMatching,
  foldGutter,
  foldKeymap,
  HighlightStyle,
} from "@codemirror/language";
import { markdown } from "@codemirror/lang-markdown";
import { javascript } from "@codemirror/lang-javascript";
import { json } from "@codemirror/lang-json";
import { css } from "@codemirror/lang-css";
import { html } from "@codemirror/lang-html";
import { python } from "@codemirror/lang-python";
import { sql } from "@codemirror/lang-sql";
import { yaml } from "@codemirror/lang-yaml";
import { oneDark } from "@codemirror/theme-one-dark";
import { searchKeymap, highlightSelectionMatches } from "@codemirror/search";
import { autocompletion, completionKeymap, closeBrackets, closeBracketsKeymap } from "@codemirror/autocomplete";
import { tags } from "@lezer/highlight";

export type EditorLanguage =
  | "markdown" | "json" | "javascript" | "typescript" | "typescriptreact"
  | "css" | "html" | "python" | "sql" | "yaml" | "plain";

export interface CodeMirrorEditorProps {
  value: string;
  onChange: (value: string) => void;
  language?: EditorLanguage;
  height?: string;
  readOnly?: boolean;
  onSave?: () => void;
  onCursorChange?: (line: number, col: number) => void;
}

// Cyberpunk theme matching --bw-* CSS variables
const cyberpunkTheme = EditorView.theme(
  {
    "&": {
      backgroundColor: "var(--bw-bg)",
      color: "var(--bw-fg)",
      fontFamily: "var(--font-mono), monospace",
      fontSize: "13px",
    },
    ".cm-content": {
      caretColor: "var(--bw-accent)",
      padding: "8px 0",
    },
    ".cm-cursor, .cm-dropCursor": {
      borderLeftColor: "var(--bw-accent)",
      borderLeftWidth: "2px",
    },
    "&.cm-focused .cm-selectionBackground, .cm-selectionBackground, .cm-content ::selection": {
      backgroundColor: "rgba(0, 240, 255, 0.2)",
    },
    ".cm-activeLine": {
      backgroundColor: "rgba(0, 240, 255, 0.05)",
    },
    ".cm-activeLineGutter": {
      backgroundColor: "rgba(0, 240, 255, 0.1)",
    },
    ".cm-gutters": {
      backgroundColor: "var(--bw-surface)",
      color: "var(--bw-muted)",
      borderRight: "1px solid var(--bw-border)",
    },
    ".cm-lineNumbers .cm-gutterElement": {
      padding: "0 8px 0 4px",
      minWidth: "32px",
    },
    ".cm-foldGutter .cm-gutterElement": {
      padding: "0 4px",
    },
    "&.cm-focused": {
      outline: "none",
    },
    ".cm-scroller": {
      overflow: "auto",
    },
    ".cm-matchingBracket": {
      backgroundColor: "rgba(0, 240, 255, 0.3)",
      color: "var(--bw-accent)",
      outline: "1px solid var(--bw-accent)",
    },
    ".cm-nonmatchingBracket": {
      backgroundColor: "rgba(255, 0, 60, 0.3)",
      color: "var(--bw-danger)",
    },
    ".cm-searchMatch": {
      backgroundColor: "rgba(252, 238, 10, 0.3)",
      outline: "1px solid var(--bw-warn)",
    },
    ".cm-searchMatch.cm-searchMatch-selected": {
      backgroundColor: "rgba(252, 238, 10, 0.5)",
    },
    ".cm-selectionMatch": {
      backgroundColor: "rgba(0, 240, 255, 0.15)",
    },
    ".cm-foldPlaceholder": {
      backgroundColor: "var(--bw-surface-2)",
      border: "1px solid var(--bw-border)",
      color: "var(--bw-muted)",
      padding: "0 4px",
      margin: "0 2px",
    },
    ".cm-tooltip": {
      backgroundColor: "var(--bw-surface)",
      border: "1px solid var(--bw-border)",
      boxShadow: "var(--bw-glow)",
    },
    ".cm-tooltip.cm-tooltip-autocomplete": {
      "& > ul > li": {
        padding: "4px 8px",
      },
      "& > ul > li[aria-selected]": {
        backgroundColor: "rgba(0, 240, 255, 0.2)",
        color: "var(--bw-accent)",
      },
    },
    ".cm-panels": {
      backgroundColor: "var(--bw-surface)",
      borderTop: "1px solid var(--bw-border)",
    },
    ".cm-panels.cm-panels-top": {
      borderBottom: "1px solid var(--bw-border)",
      borderTop: "none",
    },
    ".cm-panel.cm-search": {
      padding: "8px",
      backgroundColor: "var(--bw-surface)",
      "& input, & button, & label": {
        fontSize: "12px",
        fontFamily: "var(--font-mono)",
      },
      "& input": {
        backgroundColor: "var(--bw-bg)",
        border: "1px solid var(--bw-border)",
        color: "var(--bw-fg)",
        padding: "4px 8px",
        outline: "none",
        "&:focus": {
          borderColor: "var(--bw-accent)",
        },
      },
      "& button": {
        backgroundColor: "var(--bw-surface-2)",
        border: "1px solid var(--bw-border)",
        color: "var(--bw-fg)",
        padding: "4px 8px",
        cursor: "pointer",
        "&:hover": {
          backgroundColor: "rgba(0, 240, 255, 0.1)",
        },
      },
      "& label": {
        color: "var(--bw-muted)",
      },
    },
  },
  { dark: true }
);

// Custom syntax highlighting
const cyberpunkHighlight = HighlightStyle.define([
  { tag: tags.heading, color: "var(--bw-accent)", fontWeight: "bold" },
  { tag: tags.heading1, fontSize: "1.4em" },
  { tag: tags.heading2, fontSize: "1.2em" },
  { tag: tags.heading3, fontSize: "1.1em" },
  { tag: tags.strong, color: "var(--bw-warn)", fontWeight: "bold" },
  { tag: tags.emphasis, color: "var(--bw-accent-2)", fontStyle: "italic" },
  { tag: tags.link, color: "var(--bw-accent)", textDecoration: "underline" },
  { tag: tags.url, color: "var(--bw-accent-2)" },
  { tag: tags.keyword, color: "#ff79c6" },
  { tag: tags.comment, color: "var(--bw-muted)", fontStyle: "italic" },
  { tag: tags.string, color: "var(--bw-success)" },
  { tag: tags.number, color: "#bd93f9" },
  { tag: tags.bool, color: "#bd93f9" },
  { tag: tags.null, color: "#bd93f9" },
  { tag: tags.propertyName, color: "var(--bw-accent)" },
  { tag: tags.variableName, color: "var(--bw-fg)" },
  { tag: tags.function(tags.variableName), color: "var(--bw-warn)" },
  { tag: tags.definition(tags.variableName), color: "var(--bw-warn)" },
  { tag: tags.typeName, color: "var(--bw-accent)" },
  { tag: tags.className, color: "var(--bw-accent)" },
  { tag: tags.operator, color: "#ff79c6" },
  { tag: tags.punctuation, color: "var(--bw-muted)" },
  { tag: tags.bracket, color: "var(--bw-fg)" },
  { tag: tags.meta, color: "var(--bw-muted)" },
  { tag: tags.quote, color: "var(--bw-muted)", fontStyle: "italic" },
  { tag: tags.list, color: "var(--bw-warn)" },
  { tag: tags.monospace, color: "var(--bw-success)", fontFamily: "var(--font-mono)" },
]);

function getLanguageExtension(language: EditorLanguage | undefined) {
  switch (language) {
    case "markdown":
      return markdown();
    case "json":
      return json();
    case "javascript":
      return javascript({ jsx: true });
    case "typescript":
    case "typescriptreact":
      return javascript({ typescript: true, jsx: true });
    case "css":
      return css();
    case "html":
      return html();
    case "python":
      return python();
    case "sql":
      return sql();
    case "yaml":
      return yaml();
    case "plain":
    default:
      return [];
  }
}

export function CodeMirrorEditor({
  value,
  onChange,
  language = "plain",
  height = "100%",
  readOnly = false,
  onSave,
  onCursorChange,
}: CodeMirrorEditorProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef<EditorView | null>(null);
  const languageCompartment = useRef(new Compartment());
  const readOnlyCompartment = useRef(new Compartment());

  // Stable callbacks
  const onChangeRef = useRef(onChange);
  const onSaveRef = useRef(onSave);
  const onCursorChangeRef = useRef(onCursorChange);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    onSaveRef.current = onSave;
  }, [onSave]);

  useEffect(() => {
    onCursorChangeRef.current = onCursorChange;
  }, [onCursorChange]);

  // Initialize editor
  useEffect(() => {
    if (!containerRef.current) return;

    const saveKeymap = keymap.of([
      {
        key: "Mod-s",
        preventDefault: true,
        run: () => {
          onSaveRef.current?.();
          return true;
        },
      },
    ]);

    const updateListener = EditorView.updateListener.of((update) => {
      if (update.docChanged) {
        const doc = update.state.doc.toString();
        onChangeRef.current(doc);
      }

      if (update.selectionSet && onCursorChangeRef.current) {
        const pos = update.state.selection.main.head;
        const line = update.state.doc.lineAt(pos);
        onCursorChangeRef.current(line.number, pos - line.from + 1);
      }
    });

    const extensions = [
      // Core editing
      history(),
      drawSelection(),
      dropCursor(),
      EditorState.allowMultipleSelections.of(true),
      indentOnInput(),
      bracketMatching(),
      closeBrackets(),
      autocompletion(),
      rectangularSelection(),
      crosshairCursor(),
      highlightActiveLine(),
      highlightActiveLineGutter(),
      highlightSelectionMatches(),

      // Gutters
      lineNumbers(),
      foldGutter(),

      // Keymaps
      keymap.of([
        ...closeBracketsKeymap,
        ...defaultKeymap,
        ...searchKeymap,
        ...historyKeymap,
        ...foldKeymap,
        ...completionKeymap,
        indentWithTab,
      ]),
      saveKeymap,

      // Tab size
      EditorState.tabSize.of(2),

      // Line wrapping
      EditorView.lineWrapping,

      // Theme and highlighting
      cyberpunkTheme,
      syntaxHighlighting(cyberpunkHighlight),
      syntaxHighlighting(defaultHighlightStyle, { fallback: true }),

      // Language (in compartment for dynamic switching)
      languageCompartment.current.of(getLanguageExtension(language)),

      // Read-only (in compartment for dynamic switching)
      readOnlyCompartment.current.of(EditorState.readOnly.of(readOnly)),

      // Update listener
      updateListener,
    ];

    const state = EditorState.create({
      doc: value,
      extensions,
    });

    const view = new EditorView({
      state,
      parent: containerRef.current,
    });

    viewRef.current = view;

    // Initial cursor position callback
    if (onCursorChangeRef.current) {
      onCursorChangeRef.current(1, 1);
    }

    return () => {
      view.destroy();
      viewRef.current = null;
    };
    // Only run on mount - value updates handled separately
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update document when value prop changes (external updates)
  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;

    const currentDoc = view.state.doc.toString();
    if (currentDoc !== value) {
      view.dispatch({
        changes: {
          from: 0,
          to: currentDoc.length,
          insert: value,
        },
      });
    }
  }, [value]);

  // Update language when prop changes
  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;

    view.dispatch({
      effects: languageCompartment.current.reconfigure(getLanguageExtension(language)),
    });
  }, [language]);

  // Update read-only when prop changes
  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;

    view.dispatch({
      effects: readOnlyCompartment.current.reconfigure(EditorState.readOnly.of(readOnly)),
    });
  }, [readOnly]);

  return (
    <div
      ref={containerRef}
      className="codemirror-container border border-[var(--bw-border)] rounded-sm"
      style={{ height, overflow: "auto" }}
    />
  );
}

export default CodeMirrorEditor;
